import { DEBUG_NAMESPACE, useDebug } from '@/composables/useDebug';
import { defineStore } from 'pinia';
import { Api } from '@/Api';
import { TrackEvents } from '@/composables/useTrack';
import { useViewerItemStore } from './useViewerItemStore';

export type MeetingShareSendStatus = 'sending' | 'sent';
export type MeetingShareSource = 'send_all' | 'individual';

export interface MeetingShareStoreState {
  // Session-only sent/sending flags keyed by lowercased attendee email.
  sendStatusByEmail: Record<string, MeetingShareSendStatus>;
}

const { debug } = useDebug(DEBUG_NAMESPACE.STORE_MEETING_SHARE);

// The event is attributed to the item owner by Web::TrackingController via the
// `hashid` property; keys are snake_cased by the request wrapper. The email
// itself is a Customer.io campaign triggered on this event — nothing else to do.
const shareProperties = (itemStore: ReturnType<typeof useViewerItemStore>, email: string, source: MeetingShareSource) => {
  const attendee = itemStore.meetingAttendees.find(candidate => candidate.email?.toLowerCase() === email);

  return {
    hashid: itemStore.id,
    shareSource: source,
    sharedWithEmail: email,
    sharedWithName: attendee?.name,
    isOrganizer: attendee?.isOrganizer,
    isInternal: attendee?.isInternal,
    itemUrl: itemStore.shareLink,
    meetingTitle: itemStore.meetingInfo?.title,
    meetingStartsAt: itemStore.meetingInfo?.startsAt,
    attendeeCount: itemStore.meetingAttendees.length,
  };
};

export const useMeetingShareStore = defineStore('meetingShareStore', {
  state: () =>
    ({
      sendStatusByEmail: {},
    }) as MeetingShareStoreState,
  actions: {
    async share(emails: string[], source: MeetingShareSource) {
      const itemStore = useViewerItemStore();

      if (!itemStore.id) {
        return Promise.reject('Missing id');
      }

      if (!emails.length) {
        return Promise.reject('Missing emails');
      }

      const normalizedEmails = emails.map(email => email.toLowerCase());

      normalizedEmails.forEach(email => {
        this.sendStatusByEmail[email] = 'sending';
      });

      const results = await Promise.allSettled(
        normalizedEmails.map(email => Api.track.create(TrackEvents.MEETING_SHARE_WITH_ATTENDEES, shareProperties(itemStore, email, source)))
      );

      const failures: unknown[] = [];

      results.forEach((result, index) => {
        const email = normalizedEmails[index];

        if (result.status === 'fulfilled') {
          this.sendStatusByEmail[email] = 'sent';
        } else {
          // Clear only the failed recipient so it can be retried; successful
          // sends keep their flag so a retry can't duplicate the email.
          delete this.sendStatusByEmail[email];
          failures.push(result.reason);
        }
      });

      if (failures.length) {
        debug('Cannot share meeting recording', failures);
        throw failures[0];
      }
    },
  },
  getters: {
    isSent: (state: MeetingShareStoreState) => (email: string) => state.sendStatusByEmail[email.toLowerCase()] === 'sent',
    isSending: (state: MeetingShareStoreState) => (email: string) => state.sendStatusByEmail[email.toLowerCase()] === 'sending',
  },
});
